/**
 * Categorizes upload failures into actionable buckets so the UI can show a
 * short title (for the chip) plus a clearer message (for tooltip / a11y).
 *
 * The backend (`apps/files/views.py::upload_file`) returns serializer errors
 * shaped as `{ file: ["<message>"] }` on 400, or `{ detail: "<message>" }` for
 * other framework errors. Network/CDN failures may return non-JSON bodies, so
 * the helpers below are defensive about parsing.
 */

import { MAX_FILE_SIZE_MB } from '@/lib/files/constants';

export type UploadErrorCategory =
  | 'too_large'
  | 'unsupported'
  | 'network'
  | 'server'
  | 'unknown';

export interface UploadErrorInfo {
  category: UploadErrorCategory;
  /** Short label rendered on the failed chip (≤24 chars). */
  title: string;
  /** Longer message for screen-reader / tooltip use. */
  message: string;
}

const TOO_LARGE_MARKERS = ['too large', 'must be under', 'file size'];
const UNSUPPORTED_MARKERS = [
  'not allowed',
  'not supported',
  'allowed extensions',
  'unable to determine file type',
];

const TITLE_BY_CATEGORY: Record<UploadErrorCategory, string> = {
  too_large: 'Too large',
  unsupported: 'Unsupported file',
  network: 'Connection issue',
  server: 'Server error',
  unknown: "Couldn't upload",
};

const GENERIC_MESSAGE_BY_CATEGORY: Record<UploadErrorCategory, string> = {
  too_large: `This file is over the ${MAX_FILE_SIZE_MB} MB limit.`,
  unsupported: 'This file type is not supported.',
  network: 'Network connection lost. Check your connection and try again.',
  server: 'Something went wrong on our side. Try again in a moment.',
  unknown: 'Something went wrong. Try again.',
};

function classifyMessage(raw: string): UploadErrorCategory {
  const lower = raw.toLowerCase();
  if (TOO_LARGE_MARKERS.some(marker => lower.includes(marker))) {
    return 'too_large';
  }
  if (UNSUPPORTED_MARKERS.some(marker => lower.includes(marker))) {
    return 'unsupported';
  }
  return 'unknown';
}

function infoFor(
  category: UploadErrorCategory,
  message: string
): UploadErrorInfo {
  return {
    category,
    title: TITLE_BY_CATEGORY[category],
    message,
  };
}

/**
 * Categorize an upload failure from a status code and a raw response body
 * string. Shared core for both the `fetch` Response path and the XHR path
 * (which only has `status` + `responseText`, not a `Response` object).
 *
 * Defensive against malformed JSON, HTML error pages, and missing fields.
 */
export function categorizeUploadErrorBody(
  status: number,
  rawBody: string | undefined,
  statusText?: string
): UploadErrorInfo {
  // Status-driven defaults — overridden by parsed body if available.
  let category: UploadErrorCategory = 'server';
  let message = '';

  if (status === 413) {
    category = 'too_large';
    message = `This file is over the ${MAX_FILE_SIZE_MB} MB limit.`;
  } else if (status === 415) {
    category = 'unsupported';
    message = 'This file type is not supported.';
  } else if (status >= 500) {
    category = 'server';
    message = 'Something went wrong on our side. Try again in a moment.';
  } else if (status >= 400) {
    category = 'unknown';
    message = 'The server rejected this file.';
  }

  // Try to enrich with body content. JSON parsing can throw if the body is
  // non-JSON (e.g. an HTML error page from a CDN/proxy).
  if (rawBody) {
    try {
      const body = JSON.parse(rawBody);
      const fromArray = Array.isArray(body?.file) ? body.file[0] : null;
      const apiMessage =
        (typeof fromArray === 'string' && fromArray) ||
        (typeof body?.detail === 'string' && body.detail) ||
        (typeof body?.error === 'string' && body.error) ||
        '';
      if (apiMessage) {
        message = apiMessage;
        const classified = classifyMessage(apiMessage);
        if (classified !== 'unknown') {
          category = classified;
        } else if (status < 500) {
          category = 'unknown';
        }
      }
    } catch {
      // Non-JSON body. Fall back to the status-based message we already set.
    }
  }

  if (!message) {
    message = `Upload failed (${status} ${statusText || 'error'}).`;
  }

  return infoFor(category, message);
}

/**
 * Parse a non-OK fetch Response into a categorized upload error.
 *
 * Defensive against malformed JSON, HTML error pages, and missing fields.
 */
export async function categorizeUploadResponse(
  response: Response
): Promise<UploadErrorInfo> {
  const raw = await response
    .clone()
    .text()
    .catch(() => undefined);
  return categorizeUploadErrorBody(response.status, raw, response.statusText);
}

/**
 * Categorize an exception thrown before/during the request (network failure,
 * abort, CORS, etc.). Treats `TypeError` from `fetch` as a network error.
 */
export function categorizeNetworkError(err: unknown): UploadErrorInfo {
  // `fetch` throws TypeError for network failures (DNS, offline, CORS preflight).
  if (err instanceof TypeError) {
    return infoFor(
      'network',
      'Network connection lost. Check your connection and try again.'
    );
  }
  if (err instanceof Error && err.name === 'AbortError') {
    return infoFor('network', 'Upload was cancelled.');
  }
  if (err instanceof Error && err.message) {
    const category = classifyMessage(err.message);
    return infoFor(category, GENERIC_MESSAGE_BY_CATEGORY[category]);
  }
  return infoFor('unknown', 'Something went wrong. Try again.');
}
